import { COMPANY_FIELD_SETTINGS } from './company-field-settings';
import { COMPANY_SETTINGS } from './registry';
import type {
  CompanyFieldKey,
  CompanyFieldSettingDefinition,
  SettingDefinition,
  SettingControl,
  SettingOption,
  SettingPage,
  SettingPermission,
  SettingSurface,
} from './registry.types';
import type { CompanySettingKey } from './schema';

/** A registry entry, whichever of the two records it came from. */
export type AnySettingDefinition =
  | SettingDefinition
  | CompanyFieldSettingDefinition;

export type AnySettingKey = CompanySettingKey | CompanyFieldKey;

export type SettingGroup = {
  readonly group: string;
  readonly items: readonly AnySettingDefinition[];
};

export type SettingSearchGroup = {
  readonly page: SettingPage;
  readonly items: readonly AnySettingDefinition[];
};

/**
 * The pages the settings search offers results from, in the order it shows
 * them. `internal` is absent on purpose: those entries are reachable from
 * internal tools only and must not surface in the customer-facing palette.
 */
export const SEARCHABLE_PAGE_ORDER: readonly SettingPage[] = [
  'general',
  'dispatch',
  'orders',
  'billing',
  'warehouse',
  'route-defaults',
];

const BY_KEY: Record<AnySettingKey, AnySettingDefinition> = {
  ...COMPANY_SETTINGS,
  ...COMPANY_FIELD_SETTINGS,
};

const ALL_SETTINGS: readonly AnySettingDefinition[] = Object.values(BY_KEY);

/** Both records as one list, config keys first. */
export const allSettings = (): readonly AnySettingDefinition[] => ALL_SETTINGS;

export const definitionFor = (key: AnySettingKey): AnySettingDefinition =>
  BY_KEY[key];

const BY_ANY_KEY: ReadonlyMap<string, AnySettingDefinition> = new Map(
  ALL_SETTINGS.map((setting) => [setting.key, setting]),
);

/**
 * The registry entry for a key that is only known to be a string -- one read
 * back from storage rather than written in code, such as a change-log row's
 * `key`. Absent when the registry no longer describes it.
 *
 * {@link definitionFor} covers keys the compiler has already narrowed to
 * {@link AnySettingKey}; both registries `satisfies Record<Key, Definition>`,
 * so it is total for every value of that type and never returns `undefined`.
 * Indexing either record with an arbitrary string does not typecheck, and
 * widening the key to `string` would make the lookup partial, so a caller
 * holding a stored key needs this function rather than a cast.
 */
export const findDefinition = (key: string): AnySettingDefinition | undefined =>
  BY_ANY_KEY.get(key);

/**
 * The options a multi-select offers, given the current value of the setting
 * its `gatedOptions` names. Callers pass the draft value, not the saved one, so
 * turning the gate on offers its options before anything is saved.
 */
export const multiSelectOptionsFor = (
  control: Extract<SettingControl, { type: 'multi-select' }>,
  gateValue: unknown,
): readonly SettingOption[] =>
  control.gatedOptions !== undefined &&
  gateValue === control.gatedOptions.value
    ? [...control.options, ...control.gatedOptions.options]
    : control.options;

/**
 * The settings on one page, grouped by card. Groups come back in the order
 * their first member appears in the registry, so the card order is editable by
 * moving an entry rather than by maintaining a second list.
 */
export const settingsForPage = (page: SettingPage): readonly SettingGroup[] => {
  const groups = new Map<string, AnySettingDefinition[]>();
  for (const setting of ALL_SETTINGS) {
    if (setting.page !== page) {
      continue;
    }
    const existing = groups.get(setting.group);
    if (existing === undefined) {
      groups.set(setting.group, [setting]);
    } else {
      existing.push(setting);
    }
  }
  return [...groups].map(([group, items]) => ({ group, items }));
};

/**
 * Every setting that takes effect on one surface, entries no user can edit
 * included. Unlike {@link searchSettings} this is deliberately total: the
 * question it answers is what changes a surface's behaviour, and 40 of the
 * entries that do are reachable from internal tools only. A customer-facing
 * palette wants {@link searchSettings} or {@link settingsForPage} instead, and
 * any caller showing these to a user owns the `exposedToUser` filter — page is
 * not a proxy for it.
 */
export const settingsBySurface = (
  surface: SettingSurface,
): readonly AnySettingDefinition[] =>
  ALL_SETTINGS.filter((setting) => setting.surfaces.includes(surface));

const matchesQuery = (
  setting: AnySettingDefinition,
  lowercaseQuery: string,
): boolean =>
  [setting.label, setting.description, setting.key, setting.group].some(
    (field) => field.toLowerCase().includes(lowercaseQuery),
  );

/**
 * Case-insensitive substring search over label, description, key and group,
 * grouped by page in {@link SEARCHABLE_PAGE_ORDER}. Only settings a user can
 * actually edit are returned: a hit needs `exposedToUser`, not merely a
 * customer-facing page. A blank query returns no groups rather than the whole
 * catalog, and a page with no hit is omitted.
 */
export const searchSettings = (
  query: string,
): readonly SettingSearchGroup[] => {
  const lowercaseQuery = query.trim().toLowerCase();
  if (lowercaseQuery.length === 0) {
    return [];
  }
  const results: SettingSearchGroup[] = [];
  for (const page of SEARCHABLE_PAGE_ORDER) {
    const items = ALL_SETTINGS.filter(
      (setting) =>
        setting.page === page &&
        setting.exposedToUser &&
        matchesQuery(setting, lowercaseQuery),
    );
    if (items.length > 0) {
      results.push({ page, items });
    }
  }
  return results;
};

const permissionIdentity = (permission: SettingPermission): string =>
  'level' in permission
    ? `${permission.resource}/${permission.level}`
    : permission.resource;

/**
 * The distinct permissions a caller must hold to write every given key, in the
 * order the keys were supplied. Callers check each one once rather than once
 * per key.
 */
export const permissionsRequiredFor = (
  keys: readonly AnySettingKey[],
): readonly SettingPermission[] => {
  const seen = new Set<string>();
  const permissions: SettingPermission[] = [];
  for (const key of keys) {
    const { permission } = definitionFor(key);
    const identity = permissionIdentity(permission);
    if (seen.has(identity)) {
      continue;
    }
    seen.add(identity);
    permissions.push(permission);
  }
  return permissions;
};
